import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { match } from 'ts-pattern';
import {
  ContactInteractionChannelResourceUnion,
  WhatsappTemplate,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import { FlexRow } from 'src/components/layout/flex-row';
import { useConversationSendWhatsappTemplateMutation } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/channels/whatsapp/whatsapp-template/queries';
import { whatsappTemplateParameterInvalidValueRegex } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/channels/whatsapp/whatsapp-template/whatsapp-template-parameter';
import { publishEvent } from 'src/utils/events/pub-sub';

export type WhatsAppTemplateTextParameter = {
  type: 'TEXT';
  text: string;
};

type WhatsAppTemplateTextParameterWithName = WhatsAppTemplateTextParameter & {
  parameterName: string;
};

export type WhatsappTemplateParameterInput = {
  key: string;
  hasError?: boolean;
  value?: WhatsAppTemplateTextParameter;
};

export type WhatsappTemplateButtonParameterInput = {
  type: string;
  subType: string;
};

export type WhatsappTemplateData = {
  header: WhatsappTemplateParameterInput[];
  body: WhatsappTemplateParameterInput[];
  button: WhatsappTemplateButtonParameterInput | null;
  rawBody?: string;
  rawHeader?: string;
  rawFooter?: string;
  bodyParamsCount: number;
  headerParamsCount: number;
};

export const templateParametersDefaultValue: WhatsappTemplateData = {
  body: [],
  header: [],
  button: null,
  rawBody: undefined,
  rawHeader: undefined,
  rawFooter: undefined,
  headerParamsCount: 0,
  bodyParamsCount: 0,
};

const getComponentWithAppliedParameters = (
  rawMessage: string | undefined,
  parameters: WhatsappTemplateParameterInput[],
) => {
  if (!rawMessage) {
    return undefined;
  }

  let resolvedComponent = rawMessage;

  parameters.forEach((param) => {
    if (param.value) {
      resolvedComponent = resolvedComponent.replace(param.key, param.value?.text);
    }
  });

  return resolvedComponent;
};

const positionalWhatsappParamRegex = /{{(\d+)}}/g;

export const getWhatsappTemplateParamName = (key: string) =>
  key.replace('{{', '').replace('}}', '');

const isValidWhatsappParamValue = (value?: string) =>
  value &&
  Boolean(value.trim().length) &&
  !whatsappTemplateParameterInvalidValueRegex.test(value);

const ActionItems = styled(FlexRow)`
  align-items: center;
  gap: 10px;

  & span {
    width: 1px;
    height: 15px;
    background-color: ${({ theme }) => theme.colors.flint};
  }

  & a {
    white-space: nowrap;
    font-size: 12px;

    ${up('md')} {
      font-size: 13px;
    }
  }
`;

type UseSendWhatsappTemplateProps = {
  selectedTemplate?: WhatsappTemplate;
  conversationId: string | null;
  selectedResource: ContactInteractionChannelResourceUnion | null;
  contactPhoneNumber?: string;
  contactAddress?: string;
  onCompleted: (newInteractionId: number) => void;
};

export const useSendWhatsappTemplate = ({
  selectedTemplate,
  conversationId,
  selectedResource,
  contactPhoneNumber,
  contactAddress,
  onCompleted,
}: UseSendWhatsappTemplateProps) => {
  const [templateParameters, setTemplateParameters] = useState<WhatsappTemplateData>(
    templateParametersDefaultValue,
  );
  const showNotification = useShowNotification();
  const { t } = useTranslation(['common', 'conversations']);

  const hasParameterErrors = useCallback(() => {
    const getParameterUpdatedValue = (parameter: WhatsappTemplateParameterInput) => ({
      ...parameter,
      hasError: !isValidWhatsappParamValue(parameter.value?.text),
    });

    const updatedBodyParams = templateParameters.body.map(getParameterUpdatedValue);
    const updatedHeaderParams = templateParameters.header.map(getParameterUpdatedValue);
    const updatedParams = [...updatedBodyParams, ...updatedHeaderParams];

    const hasEmptyValues = updatedParams.some(
      ({ value }) => !Boolean(value?.text?.trim().length),
    );
    const hasInvalidValues = updatedParams.some(
      ({ value, hasError }) => hasError && Boolean(value?.text?.trim().length),
    );

    if (!hasEmptyValues && !hasInvalidValues) {
      return false;
    }

    setTemplateParameters({
      ...templateParameters,
      body: updatedBodyParams,
      header: updatedHeaderParams,
    });

    showNotification({
      title: t(
        hasInvalidValues
          ? 'conversations:messageInput.modes.reply.messages.whatsapp.whatsappTemplate.validationMessages.invalidParameters.title'
          : 'conversations:messageInput.modes.reply.messages.whatsapp.whatsappTemplate.validationMessages.missingParameters.title',
      ),
      message: t(
        hasInvalidValues
          ? 'conversations:messageInput.modes.reply.messages.whatsapp.whatsappTemplate.validationMessages.invalidParameters.message'
          : 'conversations:messageInput.modes.reply.messages.whatsapp.whatsappTemplate.validationMessages.missingParameters.message',
      ),
      type: 'warning',
    });

    return true;
  }, [showNotification, t, templateParameters]);

  const [sendWhatsappTemplate, { loading: isSendingWhatsappTemplate }] =
    useConversationSendWhatsappTemplateMutation();

  const hasValidLength = useCallback(() => {
    const headerMaxLength = 60;
    const expectedHeader = getComponentWithAppliedParameters(
      templateParameters.rawHeader,
      templateParameters.header,
    );

    const expectedBody = getComponentWithAppliedParameters(
      templateParameters.rawBody,
      templateParameters.body,
    );

    if (expectedHeader && expectedHeader.length > headerMaxLength) {
      showNotification({
        title: t(
          'conversations:messageInput.modes.reply.messages.whatsapp.whatsappTemplate.validationMessages.headerMaxLength.title',
        ),
        message: t(
          'conversations:messageInput.modes.reply.messages.whatsapp.whatsappTemplate.validationMessages.headerMaxLength.message',
          { limit: headerMaxLength.toLocaleString() },
        ),
        type: 'warning',
      });

      return false;
    }

    if (expectedBody) {
      const hasFooterOrHeader =
        templateParameters.rawFooter || templateParameters.rawHeader;
      const bodyLengthLimit = hasFooterOrHeader ? 1024 : 32768;

      if (expectedBody.length > bodyLengthLimit) {
        showNotification({
          title: t(
            'conversations:messageInput.modes.reply.messages.whatsapp.whatsappTemplate.validationMessages.bodyMaxLength.title',
          ),
          message: t(
            'conversations:messageInput.modes.reply.messages.whatsapp.whatsappTemplate.validationMessages.bodyMaxLength.message',
            { limit: bodyLengthLimit.toLocaleString() },
          ),
          type: 'warning',
        });

        return false;
      }
    }

    return true;
  }, [
    showNotification,
    t,
    templateParameters.body,
    templateParameters.header,
    templateParameters.rawBody,
    templateParameters.rawFooter,
    templateParameters.rawHeader,
  ]);

  const handleSendTemplate = useCallback(async () => {
    if (
      isSendingWhatsappTemplate ||
      !hasValidLength() ||
      hasParameterErrors() ||
      !selectedTemplate
    ) {
      return;
    }

    const getFormattedVariables = (parameters: WhatsappTemplateParameterInput[]) => {
      return parameters
        .filter(({ value }) => value !== undefined)
        .map((parameter) => {
          const { key, value } = parameter;

          const baseParameter = {
            type: 'TEXT',
            text: value?.text,
          };

          if (key.match(positionalWhatsappParamRegex)) {
            return baseParameter;
          }

          return {
            ...baseParameter,
            parameterName: getWhatsappTemplateParamName(key),
          };
        }) as (WhatsAppTemplateTextParameter | WhatsAppTemplateTextParameterWithName)[];
    };

    const headerComponent = selectedTemplate.components.find(
      (t) =>
        t.__typename === 'WhatsappTemplateComponentHeader' ||
        t.__typename === 'WhatsappTemplateComponentMediaHeader',
    );

    const headerVariables = match(headerComponent)
      .with(
        {
          __typename: 'WhatsappTemplateComponentHeader',
        },
        () => getFormattedVariables(templateParameters.header),
      )
      .with({ __typename: 'WhatsappTemplateComponentMediaHeader' }, (template) => {
        const mediaLink = template.example.headerHandle?.[0];

        return match(template.format)
          .with('IMAGE', () => [
            {
              type: 'IMAGE',
              image: {
                link: mediaLink,
              },
            },
          ])
          .with('VIDEO', () => [
            {
              type: 'VIDEO',
              video: {
                link: mediaLink,
              },
            },
          ])
          .with('DOCUMENT', () => [
            {
              type: 'DOCUMENT',
              document: {
                link: mediaLink,
              },
            },
          ])
          .otherwise(() => []);
      })
      .otherwise(() => []);

    if (!conversationId || !selectedResource || !(contactPhoneNumber || contactAddress)) {
      return;
    }

    await sendWhatsappTemplate({
      variables: {
        resourceId: selectedResource?.id,
        templateId: selectedTemplate.id,
        bodyVariables: getFormattedVariables(templateParameters.body),
        headerVariables: headerVariables,
        button: templateParameters.button ?? null,
        conversationId,
        contactAddress: contactAddress ?? null,
        contactPhoneNumber: contactPhoneNumber ?? null,
      },
      onCompleted: ({ conversationSendWhatsappTemplate: data }) => {
        if (!data.interaction?.id) {
          return;
        }

        onCompleted(Number(data.interaction.id));

        setTemplateParameters(templateParametersDefaultValue);
        publishEvent('CONVERSATION_UPDATED', { id: conversationId });
      },
      onError: (error) => {
        const isLowResourceBalanceError = error.graphQLErrors?.some(
          (e) =>
            e.extensions?.detail.http?.response.data?.meta?.errorMeta?.code ===
            'LOW_RESOURCE_BALANCE',
        );

        if (isLowResourceBalanceError) {
          showNotification({
            title: (
              <Trans
                i18nKey="conversations:messageInput.options.whatsapp.templatePreview.errors.lowResourceBalance.title"
                values={{ phoneNumber: selectedResource?.address }}
              />
            ),
            message: t(
              'conversations:messageInput.options.whatsapp.templatePreview.errors.lowResourceBalance.details',
            ),
            type: 'warning',
            actionElements: (
              <ActionItems>
                <a
                  data-lgg-id="no-balance-notification-left-action"
                  href="https://app.360dialog.io/login/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t(
                    'conversations:messageInput.options.whatsapp.templatePreview.errors.lowResourceBalance.leftAction',
                  )}
                </a>
                <span />
                <a
                  data-lgg-id="no-balance-notification-right-action"
                  href="https://docs.360dialog.com/docs/client-hub/funds"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t(
                    'conversations:messageInput.options.whatsapp.templatePreview.errors.lowResourceBalance.rightAction',
                  )}
                </a>
              </ActionItems>
            ),
          });
        }
      },
    });
  }, [
    isSendingWhatsappTemplate,
    hasValidLength,
    hasParameterErrors,
    selectedTemplate,
    conversationId,
    selectedResource,
    contactPhoneNumber,
    contactAddress,
    sendWhatsappTemplate,
    templateParameters.body,
    templateParameters.button,
    templateParameters.header,
    onCompleted,
    showNotification,
    t,
  ]);

  return {
    handleSendTemplate,
    isSendingWhatsappTemplate,
    templateParameters,
    setTemplateParameters,
  };
};
